import {
  MetricChartPlotStyle,
  TaggedUplotSeries,
  UplotData,
  UplotTimeSeries,
  UplotTimeSeriesChart,
} from "@/components/charts/chart.types";
import { withDefault } from "@/utils/array-utils";
import {
  getCarbonColorPaletteDark,
  getCarbonColorPaletteLight,
  getColorOverrideForSeries,
} from "@/utils/color-util";
import { isDefined } from "@/utils/function-utils";
import { useIsDarkMode } from "@/utils/hooks/use-is-dark-mode";
import {
  chartUnitsFromChart,
  formatSeriesWithLabel,
  formatValueWithLabel,
  unitsAreDisplayedInline,
} from "@/utils/metrics/metrics-graphs";
import { staticEstimateSVGTextWidth } from "@/utils/string-utils";
import { maxBy } from "lodash";
import { useMemo } from "react";
import uPlot from "uplot";

export type { MetricChartPlotStyle };
export enum MetricChartPlotStyleEnum {
  LINE = "line",
  BOX_PLOT = "box-plot",
  BAR = "bar",
  STACKED_BAR = "stacked-bar",
  DISTRIBUTION = "distribution",
}

export interface SeriesColorOptions {
  label?: string;
  colorOverrides?: Record<string, string>;
  isDarkMode: boolean;
}

export function seriesIndexToColor(
  index: number,
  { label, colorOverrides, isDarkMode }: SeriesColorOptions,
): string {
  const override = label ? getColorOverrideForSeries(label, colorOverrides) : null;
  const niceColors = isDarkMode ? getCarbonColorPaletteDark() : getCarbonColorPaletteLight();
  return override ?? niceColors[index % niceColors.length];
}

/**
 * Defining a "gap" in a chart if the time difference between non-null two points exceeds
 * a percentage of the chart's time range (0 - 100)
 */
export interface SpanGapPercentageLimit {
  case: "percentage";
  percentageLimit: number;
}

/**
 * Defining a "gap" in a chart if the number of null points between two non-null points
 * exceeds a limit
 */
export interface SpanGapBucketCountLimit {
  case: "bucketCount";
  nullBucketLimit: number;
}

/**
 * Defining a "gap" in a chart if the time difference between two non-null points
 * exceeds an absolute seconds difference
 */
export interface SpanGapAbsoluteTimeLimit {
  case: "absoluteTime";
  timeSecLimit: number;
}

export type SpanGapLimit =
  | SpanGapPercentageLimit
  | SpanGapBucketCountLimit
  | SpanGapAbsoluteTimeLimit;

export const DEFAULT_SPAN_GAP_LIMIT = {
  case: "percentage",
  percentageLimit: 10,
} satisfies SpanGapPercentageLimit;

const DEFAULT_SERIES_STROKE_WIDTH = 1.5;
const HOVERED_SERIES_STROKE_WIDTH = 2.5;

// variant props are unique to the end use-case
// and contrast with internal state managed by the chart implementation
// use this to discover what options this hook supports.
export interface UseUplotTimeSeriesChartOptsVariantProps {
  bottomLegend?: boolean;
  disableZoom?: boolean;
  hiddenSeries?: Set<string>;
  hideLegend?: boolean;
  /** Always reserve and render a legend when the chart has at least one series. */
  showLegend?: boolean;
  hideYAxisLabel?: boolean;
  hideWindowPeriodDisplay?: boolean;
  fillZero?: boolean; // default false. Fill undefined values with zero - generally wrong!
  /**
   * Drop uPlot's auto right padding (reserved so the last x tick label can
   * overhang the plot) so the plot area runs flush to the container's right
   * edge. For charts aligned above full-width content (e.g. the logs
   * histogram over its search bar); the final time label may clip slightly
   * when a tick lands at the very edge.
   */
  flushRightPadding?: boolean;
  overrideUnits?: string | null; // override the units on the y-axis
  plotStyle?: MetricChartPlotStyle;
  spanGaps?: boolean;
  spanGapLimit?: SpanGapLimit; // if the number of null values between two non-null values is less than this limit, we will draw a line.
  syncKey?: string;
}

export interface UseUplotTimeSeriesChartOpts extends UseUplotTimeSeriesChartOptsVariantProps {
  chart: UplotTimeSeriesChart | null | undefined;
  width: number | undefined | "100%";
  height: number | undefined;
  boldSeries: ReadonlySet<string>;
  hiddenSeries?: Set<string>;
  plugins: uPlot.Plugin[];
  range: [number, number] | undefined;
  colorOverrides?: Record<string, string>;
  yMin?: number;
  yAxisOptions?: { yAxisSoftMin?: number; yAxisSoftMax?: number };
}

export interface ChartSize {
  width: number | "100%";
  height: number | "100%";
}

export interface UseTimeSeriesChartDataReturn {
  options: uPlot.Options;
  vertical: boolean;
  hasLegend: boolean;
  chartSize: ChartSize;
  unitsLabel: string | undefined;
}

/**
 * Charts at or below this width stack the legend under the plot; wider charts
 * put it beside the plot. Loading skeletons use the same decision so the
 * legend does not move when data arrives.
 */
export const isVerticalChartLayout = (width: number | "100%" | undefined): boolean =>
  width != null && width !== "100%" ? width <= 620 : false;

export const defaultData = [
  [(+new Date() - 60 * 60 * 24 * 1000) / 1000, +new Date() / 1000],
  [0, 0],
] satisfies UplotData;

export const LEGEND_WIDTH_STATIC = 180;
export const CHART_FONT_FAMILY =
  '"Chalk Dashboard Inter", "Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif';
export const CHART_MICROTEXT_FONT = `10px ${CHART_FONT_FAMILY}`;
export const CHART_GUIDE_LINE_COLOR_LIGHT = "#e4e4e7"; // zinc-200
export const CHART_GUIDE_LINE_COLOR_DARK = "#27272a"; // zinc-800

export type PickChartOpts<T extends keyof UseUplotTimeSeriesChartOpts> = Pick<
  UseUplotTimeSeriesChartOpts,
  T
>;

const useHasSinglePoint = ({ chart }: PickChartOpts<"chart">) => {
  return useMemo(() => {
    if (chart == null) {
      return undefined;
    }
    return (
      chart.series.length > 0 && // we have series
      chart.series.every((s) => s.points.length === 1) // every series has one point
    );
  }, [chart]);
};

const isSeriesWithSingleNonNullPoint = (series: UplotTimeSeries): boolean => {
  return series.points.filter(isDefined).length <= 1;
};

const useHasSeriesWithSingleNonNullPoint = ({ chart }: PickChartOpts<"chart">) => {
  return useMemo(() => {
    if (chart == null) {
      return false;
    }

    return chart.series.some((s) => isSeriesWithSingleNonNullPoint(s));
  }, [chart]);
};

export const chartToAlignedData = (
  chart: UplotTimeSeriesChart | null | undefined,
  fillZero = false,
): UplotData => {
  if (!isDefined(chart) || chart.xSeries.length === 0 || chart.series.length === 0) {
    return defaultData;
  }
  return [
    chart.xSeries,
    ...chart.series.map((s) => (fillZero ? s.points.map(withDefault(0)) : s.points)),
  ];
};

// While this hook is very simple and small, it's nice to see it
// as a named hook in react component debugging
export const useUplotAlignedData = ({
  chart,
  fillZero,
}: Pick<UseUplotTimeSeriesChartOpts, "chart" | "fillZero">) => {
  return useMemo(() => {
    if (chart == null) {
      return undefined;
    }

    return chartToAlignedData(chart, fillZero);
  }, [chart, fillZero]);
};

export const setComplicatedHideLegendCondition = (
  chart: UplotTimeSeriesChart | null | undefined,
  hiddenSeries: Set<string> | undefined,
  hideLegend: boolean | undefined,
  showLegend: boolean | undefined,
  plotStyle: MetricChartPlotStyle | undefined,
): boolean => {
  if (chart == null) {
    return false;
  }
  if (hideLegend) {
    return false;
  }
  if (plotStyle === "box-plot") {
    return false;
  }
  if (showLegend && chart.series.length > 0) {
    return true;
  }
  if (chart.series.length > 1) {
    return true;
  }

  return hiddenSeries != null && hiddenSeries.size > 0 && chart.series.length > 0;
};

export const inferWindowPeriod = (chart: UplotTimeSeriesChart): number | null => {
  if (isDefined(chart.windowDurationSeconds)) {
    return chart.windowDurationSeconds;
  }

  if (chart.xSeries.length > 1) {
    return chart.xSeries[1] - chart.xSeries[0];
  }

  return null;
};

export const useJustChartSize = ({
  bottomLegend,
  width,
  height,
  hideLegend,
  showLegend,
  plotStyle,
  chart,
  hiddenSeries,
}: PickChartOpts<
  | "bottomLegend"
  | "width"
  | "height"
  | "hideLegend"
  | "showLegend"
  | "plotStyle"
  | "chart"
  | "hiddenSeries"
>) => {
  return useMemo(() => {
    const vertical = bottomLegend || isVerticalChartLayout(width);
    const hasLegend = setComplicatedHideLegendCondition(
      chart,
      hiddenSeries,
      hideLegend,
      showLegend,
      plotStyle,
    );
    const chartSize = {
      // width: (width ?? 0) - (!hasLegend || vertical ? 0 : 300),
      width:
        width === "100%"
          ? width
          : (width ?? 0) - (!hasLegend || vertical ? 0 : LEGEND_WIDTH_STATIC),
      height: Math.max((height ?? 0) - (vertical && hasLegend ? 24 : 0), 0),
    };

    return { chartSize, vertical, hasLegend };
  }, [bottomLegend, width, height, hideLegend, showLegend, plotStyle, chart, hiddenSeries]);
};

export const defaultSeries: uPlot.Series[] = [{}, {}];
export const defaultAxes: uPlot.Axis[] = [
  { grid: { show: false } },
  {
    grid: { show: false },
    size: 80,
    stroke: "transparent",
    ticks: { show: false },
  },
];

type GapFilter = (gap: uPlot.Series.Gap | null | undefined) => boolean;

export const findGapBefore = (
  chart: UplotTimeSeriesChart,
  seriesData: (number | null | undefined)[],
  startIndex: number,
): uPlot.Series.Gap | null => {
  // if this is the earliest point, there is no gap before
  if (startIndex === 0) {
    return null;
  }

  // if the point before is defined, there is no gap
  if (isDefined(seriesData.at(startIndex - 1))) {
    return null;
  }

  //
  for (let i = startIndex - 2; i > -1; i--) {
    if (isDefined(seriesData.at(i))) {
      return [chart.xSeries[i], chart.xSeries[startIndex]];
    }
  }

  return [Number.NEGATIVE_INFINITY, chart.xSeries[startIndex]];
};

export const findGapAfter = (
  chart: UplotTimeSeriesChart,
  seriesData: (number | null | undefined)[],
  startIndex: number,
): uPlot.Series.Gap | null => {
  // if this is the latest point, there is no gap after
  if (startIndex === seriesData.length - 1) {
    return null;
  }

  // if the point after is defined, there is no gap
  if (isDefined(seriesData.at(startIndex + 1))) {
    return null;
  }

  for (let i = startIndex + 2; i < seriesData.length; i++) {
    if (isDefined(seriesData.at(i))) {
      return [chart.xSeries[startIndex], chart.xSeries[i]];
    }
  }

  return [chart.xSeries[startIndex], Number.POSITIVE_INFINITY];
};

const makeGapFilter = ({
  chart,
  spanGaps,
  spanGapLimit,
}: PickChartOpts<"chart" | "spanGaps" | "spanGapLimit">): GapFilter => {
  if (spanGaps || !isDefined(spanGapLimit) || !isDefined(chart)) {
    return () => true;
  }

  const windowPeriodSafe = inferWindowPeriod(chart);

  // Note (very important) - uplot does that gaps based on absolute pixels on the page.
  // This is insane but necessary for a generic library.
  // the gaps expected to passed in here are based on unix timestamps
  switch (spanGapLimit.case) {
    case "absoluteTime":
      return (gap) => {
        if (!gap) return false;
        const timeGap = gap[1] - gap[0];
        return timeGap > spanGapLimit.timeSecLimit;
      };
    case "bucketCount":
      return (gap) => {
        if (!gap) return false;
        if (!isDefined(windowPeriodSafe)) {
          return true;
        }
        const timeGap = gap[1] - gap[0];
        return timeGap / windowPeriodSafe > spanGapLimit.nullBucketLimit;
      };

    case "percentage": {
      const firstPoint = chart.xSeries.at(0);
      const lastPoint = chart.xSeries.at(-1);
      return (gap) => {
        if (!gap) return false;
        const timeGap = gap[1] - gap[0];

        return (
          !isDefined(firstPoint) ||
          !isDefined(lastPoint) ||
          timeGap / (lastPoint - firstPoint) > spanGapLimit.percentageLimit / 100
        );
      };
    }
    default:
      return () => true;
  }
};

export const useJustSeries = ({
  chart,
  boldSeries,
  plotStyle,
  overrideUnits,
  hiddenSeries,
  spanGaps = false,
  spanGapLimit = DEFAULT_SPAN_GAP_LIMIT,
  colorOverrides,
  seriesStrokeWidth = DEFAULT_SERIES_STROKE_WIDTH,
  hoveredSeriesStrokeWidth = HOVERED_SERIES_STROKE_WIDTH,
}: PickChartOpts<
  | "chart"
  | "boldSeries"
  | "hiddenSeries"
  | "plotStyle"
  | "overrideUnits"
  | "spanGaps"
  | "spanGapLimit"
  | "colorOverrides"
> & {
  seriesStrokeWidth?: number;
  hoveredSeriesStrokeWidth?: number;
}): uPlot.Series[] => {
  const isDark = useIsDarkMode();
  return useMemo(() => {
    if (chart == null) {
      return defaultSeries;
    }

    const dataExists = chart.series.length > 0 && chart.series[0].points.length > 0;
    const unitsLabel = chartUnitsFromChart(chart, overrideUnits, hiddenSeries);
    const gapFilter = makeGapFilter({ chart, spanGaps, spanGapLimit });

    const pointFilter: uPlot.Series.Points.Filter = (
      self: uPlot,
      seriesIdx: number,
      show: boolean,
    ) => {
      if (!show) {
        return [];
      }

      const seriesPoints = self.data[seriesIdx] as (number | null | undefined)[];
      if (spanGaps) {
        const { firstNonNullIdx, lastNonNullIdx } = seriesPoints.reduce(
          (accum, pt, idx) => ({
            ...accum,
            firstNonNullIdx: accum.firstNonNullIdx > -1 || !pt ? accum.firstNonNullIdx : idx,
            lastNonNullIdx: pt ? idx : accum.lastNonNullIdx,
          }),
          {
            firstNonNullIdx: -1,
            lastNonNullIdx: -1,
          },
        );

        if (firstNonNullIdx === -1) {
          return [];
        }

        if (firstNonNullIdx === lastNonNullIdx) {
          // For a single point, always render the point
          return [firstNonNullIdx];
        }

        // Ends of lines should have a point if it is not the edge of the map
        return [
          firstNonNullIdx !== 0 ? firstNonNullIdx : undefined,
          lastNonNullIdx !== seriesPoints.length - 1 ? lastNonNullIdx : undefined,
        ].filter(isDefined);
      }

      // Return indices that are next to a null value
      return seriesPoints
        .map((_point, idx) => idx)
        .filter((idx): boolean => {
          if (idx === 0) {
            const gapAfter = findGapAfter(chart, seriesPoints, idx);
            // Check that there is a gap afterwards that will not be filtered away
            return gapFilter(gapAfter);
          } else if (idx === seriesPoints.length - 1) {
            const gapBefore = findGapBefore(chart, seriesPoints, idx);
            // Check that there is a gap before that will not be filtered away
            return gapFilter(gapBefore);
          }

          return (
            gapFilter(findGapBefore(chart, seriesPoints, idx)) ||
            gapFilter(findGapAfter(chart, seriesPoints, idx))
          );
        });
    };

    const series: uPlot.Series[] = dataExists
      ? [
          {},
          ...chart.series.map<TaggedUplotSeries>((s, i) => {
            const label = s.label;
            const isBold = boldSeries.has(label);
            // Points-only series are annotations layered over the data, so emphasis on another
            // series never dims them.
            const isPointsOnly = s.drawStyle === "points";
            const isNotBold = boldSeries.size > 0 && !isBold && !isPointsOnly;
            const seriesColor = seriesIndexToColor(i, {
              label,
              colorOverrides,
              isDarkMode: isDark,
            });

            return {
              stroke: seriesColor,
              alpha: isNotBold ? 0.4 : 1,
              fill: plotStyle === "bar" || plotStyle === "stacked-bar" ? seriesColor : undefined,
              width:
                isBold && plotStyle !== "stacked-bar"
                  ? hoveredSeriesStrokeWidth
                  : seriesStrokeWidth,
              label: s.label,
              groupTags: s.groupTags,
              show: !hiddenSeries?.has(s.label), // if hidden series is null
              spanGaps,
              gaps: (plot, _seriesIdx, _idx0, _idx1, gaps) => {
                const fullWidth = plot.bbox.width;
                const fullRange = chart.xSeries.at(-1)! - chart.xSeries[0];
                return gaps.filter((gap) =>
                  gapFilter([(gap[0] * fullRange) / fullWidth, (gap[1] * fullRange) / fullWidth]),
                );
              },
              // A null path builder draws no line; every value is then drawn as a point below.
              ...(isPointsOnly ? { paths: () => null } : {}),
              value: (_self: uPlot, rawValue: number) => formatValueWithLabel(rawValue, unitsLabel),
              points: isPointsOnly
                ? { show: true, fill: seriesColor, size: 7 }
                : {
                    filter: pointFilter,
                    fill: isDark ? seriesColor : undefined,
                    show: true,
                  },
            };
          }),
        ].filter(isDefined)
      : defaultSeries;
    return series;
  }, [
    chart,
    overrideUnits,
    spanGaps,
    spanGapLimit,
    boldSeries,
    colorOverrides,
    plotStyle,
    hiddenSeries,
    isDark,
    seriesStrokeWidth,
    hoveredSeriesStrokeWidth,
  ]);
};

const RIGHT_ALIGNED_PLOTS: Set<MetricChartPlotStyle | undefined> = new Set(["bar", "stacked-bar"]);

export const getAlignmentForChart = ({
  plotStyle,
}: PickChartOpts<"plotStyle">): uPlot.Axis.Align => {
  if (RIGHT_ALIGNED_PLOTS.has(plotStyle)) {
    return 2;
  }

  return 1;
};

export const useJustAxes = ({
  chart,
  hideYAxisLabel,
  overrideUnits,
  plotStyle,
  hiddenSeries,
}: PickChartOpts<"chart" | "hideYAxisLabel" | "overrideUnits" | "plotStyle" | "hiddenSeries">) => {
  const isDarkMode = useIsDarkMode();
  return useMemo(() => {
    if (chart == null) {
      return defaultAxes;
    }

    const dataExists = chart.series.length > 0 && chart.series[0].points.length > 0;
    const unitsLabel = chartUnitsFromChart(chart, overrideUnits, hiddenSeries);
    const gridStroke = isDarkMode ? CHART_GUIDE_LINE_COLOR_DARK : CHART_GUIDE_LINE_COLOR_LIGHT;
    const axisStroke = isDarkMode ? "#a1a1aa" : "#000";

    const axes: uPlot.Axis[] = dataExists
      ? [
          {
            // frustrating - can't import this enum (Axis.Align.Right) because of --isolatedModules
            align: getAlignmentForChart({ plotStyle }),
            font: CHART_MICROTEXT_FONT,
            gap: 10,
            grid: { show: false },
            show: true,
            space: 50,
            stroke: axisStroke,
          },
          {
            grid: {
              show: true,
              stroke: gridStroke,
              width: 1,
            },
            gap: 10,
            values: (uPlot, ticks) => formatSeriesWithLabel(ticks, unitsLabel),
            size: (uPlot, values) => {
              // dynamically determine width of this label area based on the text
              const longestStringWidth = maxBy(
                values?.map((v) => staticEstimateSVGTextWidth(v, 62.5)),
              );
              if (longestStringWidth != null) {
                return longestStringWidth + 14; // account for gap on left and right
              } else {
                return 80;
              }
            },
            label: hideYAxisLabel || unitsAreDisplayedInline(unitsLabel) ? undefined : unitsLabel,
            labelGap: 2,
            labelSize: 18,
            font: CHART_MICROTEXT_FONT,
            labelFont: CHART_MICROTEXT_FONT,
            ticks: { show: false },
            stroke: axisStroke,
          },
        ]
      : defaultAxes;

    return axes;
  }, [chart, hideYAxisLabel, hiddenSeries, isDarkMode, overrideUnits, plotStyle]);
};

export const useChartOptions = ({
  chart,
  axes,
  chartSize,
  disableZoom,
  flushRightPadding,
  plugins,
  range,
  series,
  syncKey,
  yMin,
  yAxisOptions,
}: PickChartOpts<
  | "chart"
  | "hideLegend"
  | "disableZoom"
  | "syncKey"
  | "fillZero"
  | "flushRightPadding"
  | "range"
  | "plugins"
  | "yMin"
  | "yAxisOptions"
> & {
  chartSize: ChartSize;
  series: uPlot.Series[];
  axes: uPlot.Axis[];
}): uPlot.Options | undefined => {
  const hasSinglePoint = useHasSinglePoint({ chart });
  const hasSingleNonNullPoint = useHasSeriesWithSingleNonNullPoint({ chart });

  return useMemo(() => {
    if (chart == null) {
      return undefined;
    }

    const options: uPlot.Options = {
      ...(chartSize as Pick<uPlot.Options, "height" | "width">), // this is a hack, I really want to pass "100%" in here
      // null sides keep uPlot's auto padding.
      ...(flushRightPadding ? { padding: [null, 0, null, null] as uPlot.Padding } : undefined),
      scales: {
        ...(range
          ? {
              x: {
                auto: false,
                min: range[0],
                max: range[1],
              },
            }
          : undefined),
        ...(yMin != null
          ? {
              y: {
                range: (_u: uPlot, dataMin: number, dataMax: number) => [
                  Math.min(yMin, dataMin),
                  dataMax,
                ],
              },
            }
          : yAxisOptions?.yAxisSoftMin != null || yAxisOptions?.yAxisSoftMax != null
            ? {
                y: {
                  range: {
                    min: { soft: yAxisOptions.yAxisSoftMin, mode: 1, pad: 0.1 },
                    max: { soft: yAxisOptions.yAxisSoftMax, mode: 1, pad: 0.1 },
                  },
                },
              }
            : undefined),
      },
      cursor: {
        points:
          hasSinglePoint || hasSingleNonNullPoint
            ? {
                size: (_u, seriesIdx) => {
                  // Index is off by 1 (see useJustSeries)
                  const chartSeries = chart.series[seriesIdx - 1];
                  if (chartSeries && isSeriesWithSingleNonNullPoint(chartSeries)) {
                    return 12;
                  }
                  // Default size seems to be 6
                  return 6;
                },
              }
            : undefined,

        drag: {
          setScale: !disableZoom,
        },
        sync:
          syncKey != null
            ? {
                key: syncKey,
                setSeries: true,
                match: [(a, b) => a === b, (a, b) => a === b],
                scales: ["x", null],
              }
            : undefined,
      },
      select: {
        show: !disableZoom,
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      },
      legend: {
        show: false,
      },
      plugins: plugins,
      series: series,
      axes: axes,
    };

    return options;
  }, [
    chart,
    chartSize,
    flushRightPadding,
    range,
    yMin,
    yAxisOptions,
    hasSinglePoint,
    hasSingleNonNullPoint,
    disableZoom,
    syncKey,
    plugins,
    series,
    axes,
  ]);
};

/**
 * Computes the uplot options based on provided props, which (in theory) are easier
 * to work with
 */
export const useUplotTimeSeriesChartOptions = ({
  bottomLegend,
  width,
  height,
  chart,
  hideLegend,
  showLegend,
  hideYAxisLabel,
  boldSeries,
  disableZoom,
  hiddenSeries,
  fillZero,
  flushRightPadding,
  range,
  overrideUnits,
  plotStyle,
  plugins,
  spanGaps,
  spanGapLimit,
  syncKey,
  colorOverrides,
  yMin,
  yAxisOptions,
}: UseUplotTimeSeriesChartOpts): UseTimeSeriesChartDataReturn | undefined => {
  const unitsLabel = useMemo(() => {
    return chart ? chartUnitsFromChart(chart, overrideUnits, hiddenSeries) : undefined;
  }, [chart, hiddenSeries, overrideUnits]);

  const { chartSize, vertical, hasLegend } = useJustChartSize({
    bottomLegend,
    width,
    height,
    hideLegend,
    showLegend,
    plotStyle,
    chart,
    hiddenSeries,
  });

  const series = useJustSeries({
    chart,
    boldSeries,
    hiddenSeries,
    overrideUnits,
    plotStyle,
    spanGaps,
    spanGapLimit,
    colorOverrides,
    seriesStrokeWidth: DEFAULT_SERIES_STROKE_WIDTH,
    hoveredSeriesStrokeWidth: HOVERED_SERIES_STROKE_WIDTH,
  });

  const axes = useJustAxes({ chart, hideYAxisLabel, hiddenSeries, overrideUnits, plotStyle });

  const options = useChartOptions({
    chart,
    disableZoom,
    syncKey,
    range,
    plugins,
    series,
    axes,
    chartSize,
    hideLegend,
    fillZero,
    flushRightPadding,
    yMin,
    yAxisOptions,
  });

  if (options == null) {
    return undefined;
  }

  return {
    options,
    vertical,
    chartSize,
    hasLegend,
    unitsLabel,
  };
};
